// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexCollectionPageContentBlock } from '@components/_rolex/CollectionPageContentBlock'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexCollectionPageContentBlock as RolexCollectionPageContentBlockModel } from '@lib/kontent/models/RolexCollectionPageContentBlock'
import { RolexImage as RolexImageModel } from '@lib/kontent/models/RolexImage'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexServicingPageV72024 } from '@lib/kontent/models/RolexServicingPageV72024'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'

type RolexServicingProps = SingleItemResponse<RolexServicingPageV72024>

const ServicingYourRolexPage: NextPage<RolexServicingProps> = ({ item, modular_content }) => {
  const { locales, locale } = useRouter()
  const { t } = useTranslation('rolex')
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )
  const heroBanner = modular_content[item.elements.hero_image.modular_content[0]]
    .elements as RolexImageModel
  const finalImage = modular_content[item.elements.image.modular_content[0]]
    .elements as RolexImageModel
  const titlePlusDescription = modular_content[item.elements.title___description.modular_content[0]]
    .elements as RolexPageIntroModel
  const contentBlocks = item.elements.content_block_items.modular_content.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPageContentBlockModel[]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'rolex.serviceRolex', '/rolex')}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="servicing your rolex page">
        <RolexBreadcrumbs
          items={[]}
          currentPageTitle={t('navigation.servicing', { ns: 'rolex' })}
        />
        <div className="bg-rolexLightBeige">
          <RolexImage
            size="xl"
            mobileImage={heroBanner.mobile_image}
            desktopImage={heroBanner.desktop_image}
            className="!mb-0"
          />
          <RolexPageIntro
            title={titlePlusDescription.title.value}
            description={titlePlusDescription.description}
            titleTag={titlePlusDescription.title_tag.value[0].codename}
          />
          {contentBlocks.map((block, key) => (
            <RolexCollectionPageContentBlock
              key={key}
              {...block}
              modularContent={modular_content}
            />
          ))}
          <RolexImage
            size="xl"
            mobileImage={finalImage.mobile_image}
            desktopImage={finalImage.desktop_image}
          />
        </div>
        <KeepExploring items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<RolexServicingProps> = async ({ locale, preview }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexServicingPageV72024>({
      codename: 'servicing_your_rolex_v7_2024',
      lang: language,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default ServicingYourRolexPage
